// src/pages/percentCalculator.js
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import SlotNumber from "../components/SlotNumber";
import { fmtNum } from "../utils/fmt";
import { useToast } from "../utils/useToast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faShareNodes, faXmark } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const LME_UNIT_LABEL = {
  per_oz: "per Toz",
  per_g: "per g",
};

  const WEIGHT_UNIT_LABEL = {
    g: "g",
    kg: "kg",
  };

export default function PercentCalculator({ authed, role }) {
  const { toast, showToast } = useToast();
  const LAST_RATES_KEY = useMemo(() => {
    // include role to avoid collisions; if you have userId later, swap role -> userId
    return `percentCalc:lastRates:${role || "anon"}`;
  }, [role]);

    const LAST_DEDUCTIONS_KEY = useMemo(() => {
    return `percentCalc:lastDeductions:${role || "anon"}`;
  }, [role]);

  function persistLastDeductions(payload) {
    // payload: { pctDetach:{pt,pd,rh}, pct:{pt,pd,rh} }
    try {
      localStorage.setItem(LAST_DEDUCTIONS_KEY, JSON.stringify(payload));
    } catch {}
  }

  function readLastDeductions() {
    try {
      return safeParse(localStorage.getItem(LAST_DEDUCTIONS_KEY) || "");
    } catch {
      return null;
    }
  }


  function safeParse(json) {
    try {
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  function persistLastRates(payload) {
    // payload: { lmeUnit, lme:{pt,pd,rh}, fxRate }
    try {
      localStorage.setItem(LAST_RATES_KEY, JSON.stringify(payload));
    } catch {}
  }

  function readLastRates() {
    try {
      return safeParse(localStorage.getItem(LAST_RATES_KEY) || "");
    } catch {
      return null;
    }
  }

  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // settings modal custom selects
  const [lmeOpen, setLmeOpen] = useState(false);
  const [weightOpen, setWeightOpen] = useState(false);

  const refLmeTrigger = useRef(null);
  const refWeightTrigger = useRef(null);
  const refLmeOptOz = useRef(null);
  const refWeightOptG = useRef(null);

  // inputs
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("g");

  const [ptPpm, setPtPpm] = useState("");
  const [pdPpm, setPdPpm] = useState("");
  const [rhPpm, setRhPpm] = useState("");

  const [lmeUnit, setLmeUnit] = useState("per_oz");
  const [ptLme, setPtLme] = useState("");
  const [pdLme, setPdLme] = useState("");
  const [rhLme, setRhLme] = useState("");
  const [cap, setCap] = useState(10); // 10/50/100
  const [pageSize] = useState(20); // fixed 20
  const [nextCursor, setNextCursor] = useState(null);
  const [fxRate, setFxRate] = useState("");
  const refPctToggle = useRef(null);
  const refJumpToggle = useRef(null);
  // advanced: percent kept (82 => deduct 18%)
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pctDetach, setPctDetach] = useState({
    pt: false,
    pd: false,
    rh: false,
  });
  const [ptPct, setPtPct] = useState("");
  const [pdPct, setPdPct] = useState("");
  const [rhPct, setRhPct] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [lastEntryId, setLastEntryId] = useState(null);
  // ppm jump (hidden advanced)
  const [showPpmJump, setShowPpmJump] = useState(false);
  const [jumpDetach, setJumpDetach] = useState({
    pt: false,
    pd: false,
    rh: false,
  });
  const [ppmDetach, setPpmDetach] = useState({
    pt: false,
    pd: false,
    rh: false,
  });

  const [ptJumpPct, setPtJumpPct] = useState("");
  const [pdJumpPct, setPdJumpPct] = useState("");
  const [rhJumpPct, setRhJumpPct] = useState("");
  const [pricingMode, setPricingMode] = useState("NONE");

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const refCustomer = useRef(null);

  const refPtLme = useRef(null);
  const refPdLme = useRef(null);
  const refRhLme = useRef(null);

  const refWeight = useRef(null);

  const refPtPpm = useRef(null);
  const refPdPpm = useRef(null);
  const refRhPpm = useRef(null);

  // % deductions (pct kept inputs)
  const refPtPct = useRef(null);
  const refPdPct = useRef(null);
  const refRhPct = useRef(null);

  // ppm jump inputs
  const refPtJump = useRef(null);
  const refPdJump = useRef(null);
  const refRhJump = useRef(null);

  const refFx = useRef(null);
  const refCalcBtn = useRef(null);

  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!authed || role !== "admin") return;
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, role, search, sort]);

  useEffect(() => {
    if (!authed || role !== "admin") return;

    let cancelled = false;
    api
      .percentCalcGetSettings()
      .then((d) => {
        if (cancelled) return;
        setSettings(d.settings || {});
        const s = d.settings || {};
        if (s.lmeUnit) setLmeUnit(s.lmeUnit);
        if (s.weightUnit) setWeightUnit(s.weightUnit);
      })
      .catch((e) => showToast(e.message || "Failed to load settings", "err"));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, role]);

  useEffect(() => {
    if (!authed || role !== "admin") return;

    const saved = readLastRates();
    if (!saved) return;

    const noLmeTyped = ptLme === "" && pdLme === "" && rhLme === "";
    const noFxTyped = fxRate === "";

    if (noLmeTyped && saved.lme) {
      setLmeUnit(saved.lmeUnit || "per_oz");
      setPtLme(saved.lme.pt ?? "");
      setPdLme(saved.lme.pd ?? "");
      setRhLme(saved.lme.rh ?? "");
    }

    if (noFxTyped) {
      setFxRate(saved.fxRate == null ? "" : String(saved.fxRate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, role]);

  useEffect(() => {
  if (!showSettings) {
    setLmeOpen(false);
    setWeightOpen(false);
  }
}, [showSettings]);

  useEffect(() => {
    if (!authed || role !== "admin") return;

    const saved = readLastDeductions();
    if (!saved) return;

    const nothingTypedYet = ptPct === "" && pdPct === "" && rhPct === "";
    const noneDetachedYet = !pctDetach.pt && !pctDetach.pd && !pctDetach.rh;

    // only hydrate on first load (don’t override current session typing)
    if (nothingTypedYet && noneDetachedYet) {
      const d = saved.pctDetach || {};
      const p = saved.pct || {};

      setPctDetach({
        pt: !!d.pt,
        pd: !!d.pd,
        rh: !!d.rh,
      });

      setPtPct(p.pt == null ? "" : String(p.pt));
      setPdPct(p.pd == null ? "" : String(p.pd));
      setRhPct(p.rh == null ? "" : String(p.rh));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, role]);


  if (!authed) return <div className="card">Please login.</div>;
  if (role !== "admin") return <div className="card">Admin only.</div>;

  const num0 = (v) => {
    if (v === "" || v == null) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // saved jump % from settings
  const savedJump = settings?.ppmJumpPct || {};
  const savedPtJump = savedJump.pt ?? 0;
  const savedPdJump = savedJump.pd ?? 0;
  const savedRhJump = savedJump.rh ?? 0;

  function jumpDisplay(metal) {
    const det = jumpDetach[metal];
    if (metal === "pt") return det ? ptJumpPct : String(savedPtJump ?? 0);
    if (metal === "pd") return det ? pdJumpPct : String(savedPdJump ?? 0);
    return det ? rhJumpPct : String(savedRhJump ?? 0);
  }

  function detachJump(metal) {
    setJumpDetach((s) => {
      if (s[metal]) return s;
      if (metal === "pt") setPtJumpPct(String(savedPtJump ?? 0));
      if (metal === "pd") setPdJumpPct(String(savedPdJump ?? 0));
      if (metal === "rh") setRhJumpPct(String(savedRhJump ?? 0));
      return { ...s, [metal]: true };
    });
  }

  function onToggleEnter(e, isOpen, openFn, firstInputRef) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;

  // IMPORTANT: stop the button’s native "click on Enter"
  e.preventDefault();
  e.stopPropagation();

  // If closed: open (without toggling back) then focus first input
  if (!isOpen) {
    openFn(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusNext(firstInputRef));
    });
    return;
  }

  // If already open: just go into the section
  focusNext(firstInputRef);
}

  function onEnterRun(e) {
    if (e.key === "Enter" || e.key === "NumpadEnter") {
      e.preventDefault();
      run(); // triggers calculate directly
    }
  }

    function openSectionAndFocus(setOpen, inputRef) {
    setOpen(true);
    // wait until the DOM renders the opened section
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        focusNext(inputRef);
      });
    });
  }

  function onEnterOpenAdvanced(e) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;
  e.preventDefault();
  // open and focus the first advanced field
  openSectionAndFocus(setShowAdvanced, refPtPct);
}

function onEnterOpenPpmJump(e) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;
  e.preventDefault();
  openSectionAndFocus(setShowPpmJump, refPtJump);
}

  function focusNext(ref) {
    if (!ref?.current) return;
    // prevent scroll jumps
    ref.current.focus({ preventScroll: true });
    // optional: select existing content so typing overwrites
    ref.current.select?.();
  }

  function onSelectTriggerEnter(e, isOpen, setOpen, firstOptionRef) {
  if (e.key !== "Enter" && e.key !== "NumpadEnter") return;

  e.preventDefault();
  e.stopPropagation();

  if (!isOpen) {
    setOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusNext(firstOptionRef));
    });
    return;
  }

  focusNext(firstOptionRef);
}


  function onAdvanceKeyDown(e, nextRef) {
    // Enter should behave like "next field"
    if (e.key === "Enter" || e.key === "NumpadEnter") {
      e.preventDefault();
      focusNext(nextRef);
    }
  }

  function onAdvanceByLength(e, nextRef, maxLen) {
    const v = e.target.value ?? "";
    // remove non-digits (keep it simple; adjust if you want decimals)
    const digits = String(v).replace(/\D/g, "");
    if (digits.length >= maxLen) {
      focusNext(nextRef);
    }
  }


  function reattachJump(metal) {
    setJumpDetach((s) => ({ ...s, [metal]: false }));
    if (metal === "pt") setPtJumpPct("");
    if (metal === "pd") setPdJumpPct("");
    if (metal === "rh") setRhJumpPct("");
  }

  // effective jump % = override if detached, else saved settings
  function effectiveJumpPct(metal) {
    const s = settings?.ppmJumpPct || {};
    if (metal === "pt") return jumpDetach.pt ? num0(ptJumpPct) : num0(s.pt);
    if (metal === "pd") return jumpDetach.pd ? num0(pdJumpPct) : num0(s.pd);
    return jumpDetach.rh ? num0(rhJumpPct) : num0(s.rh);
  }

  function effectivePpmValue(enteredPpm, jumpPct) {
    const e = num0(enteredPpm);
    const j = num0(jumpPct);
    return e * (1 + j / 100);
  }

  const ptJumpEff = effectiveJumpPct("pt");
  const pdJumpEff = effectiveJumpPct("pd");
  const rhJumpEff = effectiveJumpPct("rh");

  const ptPpmEff = effectivePpmValue(ptPpm, ptJumpEff);
  const pdPpmEff = effectivePpmValue(pdPpm, pdJumpEff);
  const rhPpmEff = effectivePpmValue(rhPpm, rhJumpEff);

  function detachPpm(metal) {
    setPpmDetach((s) => ({ ...s, [metal]: true }));
  }
  function reattachPpm(metal) {
    setPpmDetach((s) => ({ ...s, [metal]: false }));
  }

  // what we show inside the PPM input (shows jumped value unless user is editing)
  function ppmDisplay(metal) {
    if (metal === "pt")
      return ppmDetach.pt ? ptPpm : String(Math.round(ptPpmEff));
    if (metal === "pd")
      return ppmDetach.pd ? pdPpm : String(Math.round(pdPpmEff));
    return ppmDetach.rh ? rhPpm : String(Math.round(rhPpmEff));
  }

  // default saved percent kept is 100 (no deduction)
  const savedPct = settings?.pctKept || {}; // optional if you want; otherwise use 100 always
  const savedPt = savedPct.pt ?? 100;
  const savedPd = savedPct.pd ?? 100;
  const savedRh = savedPct.rh ?? 100;

  function pctDisplay(metal) {
    if (metal === "pt") return pctDetach.pt ? ptPct : String(savedPt);
    if (metal === "pd") return pctDetach.pd ? pdPct : String(savedPd);
    return pctDetach.rh ? rhPct : String(savedRh);
  }

  function detachPct(metal) {
    setPctDetach((s) => {
      if (s[metal]) return s;
      if (metal === "pt") setPtPct(String(savedPt));
      if (metal === "pd") setPdPct(String(savedPd));
      if (metal === "rh") setRhPct(String(savedRh));
      return { ...s, [metal]: true };
    });
  }

  function reattachPct(metal) {
    setPctDetach((s) => {
      const nextDetach = { ...s, [metal]: false };

      // also persist the updated state
      const nextPct = {
        pt: metal === "pt" ? "" : ptPct,
        pd: metal === "pd" ? "" : pdPct,
        rh: metal === "rh" ? "" : rhPct,
      };

      persistLastDeductions({ pctDetach: nextDetach, pct: nextPct });
      return nextDetach;
    });

    if (metal === "pt") setPtPct("");
    if (metal === "pd") setPdPct("");
    if (metal === "rh") setRhPct("");
  }


  async function loadEntries(reset = true) {
    if (!authed || role !== "admin") return;
    setLoadingEntries(true);
    try {
      const d = await api.percentCalcEntries({
        search,
        sort,
        limit: pageSize,
        cap,
        cursor: reset ? "" : nextCursor || "",
      });

      const newRows = d.entries || [];
      setEntries((prev) => (reset ? newRows : [...prev, ...newRows]));
      setNextCursor(d.nextCursor || null);
    } catch (e) {
      showToast(e.message || "Failed to load entries", "err");
    } finally {
      setLoadingEntries(false);
    }
  }

  async function saveSettings() {
    try {
      const payload = {
        marginUsdPerKg: num0(settings?.marginUsdPerKg),
        // optional: store default percents if you want (otherwise remove this)
        pctKept: {
          pt: num0(settings?.pctKept?.pt ?? 100),
          pd: num0(settings?.pctKept?.pd ?? 100),
          rh: num0(settings?.pctKept?.rh ?? 100),
        },
        ppmJumpPct: {
          pt: num0(settings?.ppmJumpPct?.pt ?? 0),
          pd: num0(settings?.ppmJumpPct?.pd ?? 0),
          rh: num0(settings?.ppmJumpPct?.rh ?? 0),
        },
        lmeUnit: settings?.lmeUnit || "per_oz",
        weightUnit: settings?.weightUnit || "g",
      };
      const d = await api.percentCalcPutSettings(payload);
      setSettings(d.settings || payload);
      showToast("Settings saved", "ok");
      setShowSettings(false);
    } catch (e) {
      showToast(e.message || "Failed to save settings", "err");
    }
  }

  async function run() {
    setResult(null);

    // build percent-kept only for metals that are detached (same pattern as your ppmJump)
    const pctKept = {};
    if (pctDetach.pt) pctKept.pt = ptPct === "" ? 100 : Number(ptPct);
    if (pctDetach.pd) pctKept.pd = pdPct === "" ? 100 : Number(pdPct);
    if (pctDetach.rh) pctKept.rh = rhPct === "" ? 100 : Number(rhPct);

    const jump = {};
    if (jumpDetach.pt) jump.pt = ptJumpPct === "" ? 0 : Number(ptJumpPct);
    if (jumpDetach.pd) jump.pd = pdJumpPct === "" ? 0 : Number(pdJumpPct);
    if (jumpDetach.rh) jump.rh = rhJumpPct === "" ? 0 : Number(rhJumpPct);

    const input = {
      pricingMode,
      weight: num0(weight),
      weightUnit,

      ptPpm: num0(ptPpm),
      pdPpm: num0(pdPpm),
      rhPpm: num0(rhPpm),

      ...(Object.keys(jump).length ? { ppmJumpPct: jump } : {}),

      lmeUnit,
      lme: { pt: num0(ptLme), pd: num0(pdLme), rh: num0(rhLme) },

      fxRate: fxRate === "" ? null : Number(fxRate),

      ...(Object.keys(pctKept).length ? { deductionPct: pctKept } : {}),
    };

    try {
      const d = await api.percentCalcRun(input, customerName);
      if (!d?.result) throw new Error("No result returned");
      setResult(d.result);
      persistLastRates({
        lmeUnit,
        lme: { pt: ptLme, pd: pdLme, rh: rhLme },
        fxRate: fxRate === "" ? null : Number(fxRate),
      });
      persistLastDeductions({
        pctDetach,
        pct: { pt: ptPct, pd: pdPct, rh: rhPct },
      });
      setLastEntryId(d.entryId || null);
      await loadEntries();
      const mode = (d.result?.pricingModeApplied || "NONE").toUpperCase();
      if (mode === "MARGIN") showToast("Margin Call", "ok");
      else if (mode === "PERCENT") showToast("Percent Call", "ok");
      else showToast("Percent Call", "ok"); // NONE behaves like percent 100% with margin 0
    } catch (e) {
      showToast(e.message || "Calculation failed", "err");
    }
  }

  function loadEntryIntoForm(x) {
    const i = x?.input || {};
    const j = i.ppmJumpPct || null;

    setJumpDetach({
      pt: j?.pt != null,
      pd: j?.pd != null,
      rh: j?.rh != null,
    });
    setPtJumpPct(j?.pt == null ? "" : String(j.pt));
    setPdJumpPct(j?.pd == null ? "" : String(j.pd));
    setRhJumpPct(j?.rh == null ? "" : String(j.rh));

    setPpmDetach({ pt: false, pd: false, rh: false });
    setCustomerName(x?.label || "");
    setWeight(i.weight ?? "");
    setWeightUnit(i.weightUnit || "g");
    setPtPpm(i.ptPpm ?? "");
    setPdPpm(i.pdPpm ?? "");
    setRhPpm(i.rhPpm ?? "");
    setLmeUnit(i.lmeUnit || "per_oz");
    setPtLme(i.lme?.pt ?? "");
    setPdLme(i.lme?.pd ?? "");
    setRhLme(i.lme?.rh ?? "");
    setFxRate(i.fxRate == null ? "" : String(i.fxRate));
    setLastEntryId(x?._id || null);

    // deductionPct = pctKept overrides; you used detach logic, so:
    const pct = i.deductionPct || {};
    setPctDetach({
      pt: pct.pt != null,
      pd: pct.pd != null,
      rh: pct.rh != null,
    });
    setPtPct(pct.pt == null ? "" : String(pct.pt));
    setPdPct(pct.pd == null ? "" : String(pct.pd));
    setRhPct(pct.rh == null ? "" : String(pct.rh));

    persistLastDeductions({
      pctDetach: {
        pt: pct.pt != null,
        pd: pct.pd != null,
        rh: pct.rh != null,
      },
      pct: {
        pt: pct.pt == null ? "" : String(pct.pt),
        pd: pct.pd == null ? "" : String(pct.pd),
        rh: pct.rh == null ? "" : String(pct.rh),
      },
    });

    const mode = (x?.input?.pricingMode || "").toUpperCase();
    if (mode) setPricingMode(mode);
    else {
      // fallback: if entry has deductionPct overrides => PERCENT, else if margin snapshot > 0 => MARGIN, else NONE
      const hasPct = !!(
        x?.input?.deductionPct && Object.keys(x.input.deductionPct).length
      );
      const hasMargin = Number(x?.settingsSnapshot?.marginUsdPerKg || 0) > 0;
      setPricingMode(hasPct ? "PERCENT" : hasMargin ? "MARGIN" : "NONE");
    }

    setResult(x?.result || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
    showToast("Loaded entry into calculator", "ok");
  }

  function resetForNewRun() {
    setPricingMode("NONE");
    // keep last entered % deductions
  }


  async function deleteEntry(entry) {
    if (!entry?._id) return;

    setDeletingId(entry._id);
    try {
      await api.percentCalcDeleteEntry(entry._id);
      showToast("Entry deleted", "ok");
      setConfirmDelete(null);

      // if user deleted the last-run entry, clear it
      if (String(lastEntryId) === String(entry._id)) setLastEntryId(null);

      await loadEntries();
    } catch (e) {
      showToast(e.message || "Failed to delete entry", "err");
    } finally {
      setDeletingId(null);
    }
  }

  const r = result;
  const weightKg = r?.weightKg || 0;
  const hasLocal = !!r?.fxRate && r?.netLocal != null;

  function onShareWhatsAppDesktop() {
  if (!r) return;

  const text = buildShareText();
  const url = whatsappShareUrl(text);

  // Use location.assign to avoid popup blockers (more reliable than window.open)
  window.location.assign(url);
}

    function buildShareText() {
    if (!r) return "";

    // Final PPM values must include jumps
    const finalPtPpm = Math.round(ptPpmEff);
    const finalPdPpm = Math.round(pdPpmEff);
    const finalRhPpm = Math.round(rhPpmEff);

    const usdTotal = r.netUsd ?? 0;
    const usdPerKg = weightKg ? usdTotal / weightKg : 0;

    const lines = [];
    lines.push("Percent Calculator");

    // Weight in kg
    lines.push(`Weight: ${fmtNum(weightKg, 3)} kg`);

    // USD total and per kg
    lines.push(`USD: $${fmtNum(usdTotal, 2)} total • $${fmtNum(usdPerKg, 2)}/kg`);

    // Local total and per kg (only if available)
    if (hasLocal) {
      const localTotal = Number(r.netLocal) || 0;
      const localPerKg = weightKg ? localTotal / weightKg : 0;
      lines.push(
        `Local: ${fmtNum(localTotal, 2)} total • ${fmtNum(localPerKg, 2)}/kg`
      );
    }

    // Final PPM values (post-jump)
    lines.push(`PPM (final): Pt ${finalPtPpm} • Pd ${finalPdPpm} • Rh ${finalRhPpm}`);

    // LME values
    const unitLabel = lmeUnit === "per_g" ? "per g" : "per Toz";
    lines.push(
      `LME (${unitLabel}): Pt ${fmtNum(num0(ptLme), 2)} • Pd ${fmtNum(num0(pdLme), 2)} • Rh ${fmtNum(num0(rhLme), 2)}`
    );

    // Optional: include customer name if set
    if (customerName?.trim()) lines.push(`Customer: ${customerName.trim()}`);

    return lines.join("\n");
  }

   async function onShare() {
     if (!r) return;

     const text = buildShareText();
     const title = "Percent Calculator";

     try {
    if (navigator.share) {
      await navigator.share({ title, text });
      showToast("Shared", "ok");
      return;
    }

    // If some mobile browser doesn't support share, fallback to copy
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      showToast("Copied", "ok");
      return;
    }

    window.prompt("Copy this:", text);
     } catch (e) {
       showToast(e?.message || "Share failed", "err");
     }
   }


    function enc(s) {
    return encodeURIComponent(s || "");
  }

  // WhatsApp share (works on mobile  desktop if WhatsApp is installed / web is available)
  function whatsappShareUrl(text) {
    return `https://api.whatsapp.com/send?text=${enc(text)}`;
  }

  function isMobileDevice() {
  // Works well enough for your use-case (mobile UI vs desktop UI)
  if (typeof navigator === "undefined") return false;

  const ua = navigator.userAgent || "";
  const isTouch =
    "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // iPadOS can pretend to be Mac; touch check helps.
  return (
    /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(ua) || isTouch
  );
}

const isMobile = isMobileDevice();


  return (
    <div className="gridCalc">
      <div className="card">
        <div className="cardHeader">
          <h2>Price Calculator (%)</h2>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button
              type="button"
              className="iconBtn"
              onClick={() => setShowSettings(true)}
              aria-label="Settings"
              title="Settings"
            >
              <FontAwesomeIcon icon={faGear} />
            </button>
          </div>
        </div>

        <div className="form">
          <label>Customer name</label>
          <input
            ref={refCustomer}
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            onKeyDown={(e) => onAdvanceKeyDown(e, refPtLme)}
            placeholder="e.g. John Doe"
          />
          <label>
            LME prices{" "}
            <span className="smallNote">
              ({LME_UNIT_LABEL[lmeUnit]})
            </span>
          </label>
          <div className="row3">
            <div className="inputWithSuffix">
              <input
                ref={refPtLme}
                type="number"
                value={ptLme}
                onChange={(e) => setPtLme(e.target.value)}
                placeholder="Platinum"
                onKeyDown={(e) => onAdvanceKeyDown(e, refPdLme)}
              />
              <span className="suffix">pt</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                ref={refPdLme}
                value={pdLme}
                onChange={(e) => setPdLme(e.target.value)}
                placeholder="Palladium"
                onKeyDown={(e) => onAdvanceKeyDown(e, refRhLme)}
              />
              <span className="suffix">pd</span>
            </div>
            <div className="inputWithSuffix">
              <input
                type="number"
                ref={refRhLme}
                value={rhLme}
                onChange={(e) => setRhLme(e.target.value)}
                placeholder="Rhodium"
                onKeyDown={onEnterOpenAdvanced}
              />
              <span className="suffix">rh</span>
            </div>
          </div>

          <div className="ppmJumpToggleRow">
                <button
                ref={refPctToggle}
                type="button"
                className="ppmJumpToggle"
                onClick={() => setShowAdvanced((v) => !v)}
                onKeyDown={(e) =>
                    onToggleEnter(e, showAdvanced, setShowAdvanced, refPtPct)
                }
                aria-expanded={showAdvanced}
                title="Percent kept"
                >
                {showAdvanced ? "advanced −" : "advanced +"}
                </button>


          </div>

          {showAdvanced ? (
            <div className="ppmJumpBox">
              <label className="ppmJumpLabel">Recovery Rate %</label>
              <div className="row3">
                <div className="inputWithAffix">
                  <input
                    ref={refPtPct}
                    type="number"
                    value={pctDisplay("pt")}
                    onFocus={() => {
                      setPricingMode("PERCENT");
                      detachPct("pt");
                    }}
                      onChange={(e) => {
                        setPricingMode("PERCENT");
                        detachPct("pt");
                        const v = e.target.value;
                        setPtPct(v);

                        persistLastDeductions({
                          pctDetach: { ...pctDetach, pt: true },
                          pct: { pt: v, pd: pdPct, rh: rhPct },
                        });
                      }}

                    onKeyDown={(e) => onAdvanceKeyDown(e, refPdPct)}
                    className={pctDetach.pt ? "isDetached" : ""}
                  />

                  <button
                    type="button"
                    className="affixBtn"
                    onClick={() => reattachPct("pt")}
                    title="Use saved"
                    tabIndex={-1}
                  >
                    ↺
                  </button>
                  <span className="affixSuffix">pt</span>
                </div>

                <div className="inputWithAffix">
                  <input
                    ref={refPdPct}
                    type="number"
                    value={pctDisplay("pd")}
                    onFocus={() => {
                      setPricingMode("PERCENT");
                      detachPct("pd");
                    }}
                    onChange={(e) => {
                      setPricingMode("PERCENT");
                      detachPct("pd");
                      const v = e.target.value;
                      setPdPct(v);

                      persistLastDeductions({
                        pctDetach: { ...pctDetach, pd: true },
                        pct: { pt: ptPct, pd: v, rh: rhPct },
                      });
                    }}

                    onKeyDown={(e) => onAdvanceKeyDown(e, refRhPct)}
                    className={pctDetach.pd ? "isDetached" : ""}
                  />

                  <button
                    type="button"
                    className="affixBtn"
                    onClick={() => reattachPct("pd")}
                    title="Use saved"
                    tabIndex={-1}
                  >
                    ↺
                  </button>
                  <span className="affixSuffix">pd</span>
                </div>

                <div className="inputWithAffix">
                  <input
                    ref={refRhPct}
                    type="number"
                    value={pctDisplay("rh")}
                    onFocus={() => {
                      setPricingMode("PERCENT");
                      detachPct("rh");
                    }}
                    onChange={(e) => {
                      setPricingMode("PERCENT");
                      detachPct("rh");
                      const v = e.target.value;
                      setRhPct(v);

                      persistLastDeductions({
                        pctDetach: { ...pctDetach, rh: true },
                        pct: { pt: ptPct, pd: pdPct, rh: v },
                      });
                    }}

                    onKeyDown={(e) => onAdvanceKeyDown(e, refWeight)}
                    className={pctDetach.rh ? "isDetached" : ""}
                  />

                  <button
                    type="button"
                    className="affixBtn"
                    onClick={() => reattachPct("rh")}
                    title="Use saved"
                    tabIndex={-1}
                  >
                    ↺
                  </button>
                  <span className="affixSuffix">rh</span>
                </div>
              </div>

              <div className="smallNote" style={{ marginTop: 8 }}>
                If your Settings margin requires more deduction, the calculator
                will automatically apply extra deduction.
              </div>
            </div>
          ) : null}

          <label>Weight{" "}<span className="smallNote">        
                    ({WEIGHT_UNIT_LABEL[weightUnit]})
                  </span>
                </label>
          <div className="inputWithSuffix">
            <input
              ref={refWeight}
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              onKeyDown={(e) => onAdvanceKeyDown(e, refPtPpm)}
              placeholder="Weight"
            />
            <span className="suffix">{weightUnit}</span>
          </div>

          <label>PPM (entered)</label>
          <div className="row3">
            <div className="inputWithSuffix">
              <input
                ref={refPtPpm}
                type="number"
                value={ppmDisplay("pt")}
                onFocus={() => detachPpm("pt")}
                onBlur={() => reattachPpm("pt")}
                onChange={(e) => {
                  detachPpm("pt");
                  setPtPpm(e.target.value);
                  // optional auto-advance at 4 digits:
                  // onAdvanceByLength(e, refPdPpm, 4);
                }}
                onKeyDown={(e) => onAdvanceKeyDown(e, refPdPpm)}
                placeholder="Pt"
              />

              <span className="suffix">pt</span>
            </div>

            <div className="inputWithSuffix">
              <input
                ref={refPdPpm}
                type="number"
                value={ppmDisplay("pd")}
                onFocus={() => detachPpm("pd")}
                onBlur={() => reattachPpm("pd")}
                onChange={(e) => {
                  detachPpm("pd");
                  setPdPpm(e.target.value);
                  // onAdvanceByLength(e, refRhPpm, 4);
                }}
                onKeyDown={(e) => onAdvanceKeyDown(e, refRhPpm)}
                placeholder="Pd"
              />

              <span className="suffix">pd</span>
            </div>

            <div className="inputWithSuffix">
              <input
                ref={refRhPpm}
                type="number"
                value={ppmDisplay("rh")}
                onFocus={() => detachPpm("rh")}
                onBlur={() => reattachPpm("rh")}
                onChange={(e) => {
                  detachPpm("rh");
                  setRhPpm(e.target.value);
                }}
                onKeyDown={onEnterOpenPpmJump}
                placeholder="Rh"
              />

              <span className="suffix">rh</span>
            </div>
          </div>

          <div className="ppmJumpToggleRow">
                <button
                ref={refJumpToggle}
                type="button"
                className="ppmJumpToggle"
                onClick={() => setShowPpmJump((v) => !v)}
                onKeyDown={(e) =>
                    onToggleEnter(e, showPpmJump, setShowPpmJump, refPtJump)
                }
                aria-expanded={showPpmJump}
                title="PPM Increase %"
                >
                {showPpmJump ? "advanced −" : "advanced +"}
                </button>
          </div>

          {showPpmJump ? (
            <div className="ppmJumpBox">
              <label className="ppmJumpLabel">PPM Increase %</label>
              <div className="row3">
                <div className="inputWithAffix">
                  <input
                    ref={refPtJump}
                    type="number"
                    value={jumpDisplay("pt")}
                    onFocus={() => detachJump("pt")}
                    onChange={(e) => {
                      detachJump("pt");
                      setPtJumpPct(e.target.value);
                    }}
                    onKeyDown={(e) => onAdvanceKeyDown(e, refPdJump)}
                    className={jumpDetach.pt ? "isDetached" : ""}
                  />

                  <button
                    type="button"
                    className="affixBtn"
                    onClick={() => reattachJump("pt")}
                    title="Use saved"
                    tabIndex={-1}
                  >
                    ↺
                  </button>
                  <span className="affixSuffix">pt</span>
                </div>

                <div className="inputWithAffix">
                  <input
                    ref={refPdJump}
                    type="number"
                    value={jumpDisplay("pd")}
                    onFocus={() => detachJump("pd")}
                    onChange={(e) => {
                      detachJump("pd");
                      setPdJumpPct(e.target.value);
                    }}
                    onKeyDown={(e) => onAdvanceKeyDown(e, refRhJump)}
                    className={jumpDetach.pd ? "isDetached" : ""}
                  />

                  <button
                    type="button"
                    className="affixBtn"
                    onClick={() => reattachJump("pd")}
                    title="Use saved"
                    tabIndex={-1}
                  >
                    ↺
                  </button>
                  <span className="affixSuffix">pd</span>
                </div>

                <div className="inputWithAffix">
                  <input
                    ref={refRhJump}
                    type="number"
                    value={jumpDisplay("rh")}
                    onFocus={() => detachJump("rh")}
                    onChange={(e) => {
                      detachJump("rh");
                      setRhJumpPct(e.target.value);
                    }}
                    onKeyDown={(e) => onAdvanceKeyDown(e, refFx)}
                    className={jumpDetach.rh ? "isDetached" : ""}
                  />

                  <button
                    type="button"
                    className="affixBtn"
                    onClick={() => reattachJump("rh")}
                    title="Use saved"
                    tabIndex={-1}
                  >
                    ↺
                  </button>
                  <span className="affixSuffix">rh</span>
                </div>
              </div>
            </div>
          ) : null}

          <label>USD exchange rate (optional)</label>
          <input
            ref={refFx}
            type="number"
            value={fxRate}
            onChange={(e) => setFxRate(e.target.value)}
            onKeyDown={onEnterRun}
            placeholder="If set, shows local price too"
          />

          <button
            ref={refCalcBtn}
            type="button"
            className="btnPrimary"
            onClick={run}
            onKeyDown={onEnterRun}
          >
            Calculate
          </button>
        </div>
      </div>

      <div className="card">
        <div className="cardHeader">
          <h2>Result</h2>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div className="smallNote">{r ? "Calculated" : "—"}</div>
            {isMobile ? (
              <button
                type="button"
                className="iconBtn"
                onClick={onShare}
                disabled={!r}
                aria-label="Share result"
                title={r ? "Share" : "Calculate first"}
              >
                <FontAwesomeIcon icon={faShareNodes} />
              </button>
            ) : (
              <button
                type="button"
                className="iconBtn"
                onClick={onShareWhatsAppDesktop}
                disabled={!r}
                aria-label="Share on WhatsApp"
                title={r ? "Share on WhatsApp" : "Calculate first"}
              >
                <FontAwesomeIcon icon={faWhatsapp} />
              </button>
            )}

          </div>
        </div>

        {r ? (
          <div className="appear">
            <div className="result4Head">
              <div className="result4HeadCell">USD</div>
              <div className="result4HeadCell result4HeadRight">Local</div>
            </div>

            <div className="result4Grid">
              <div className="resultCell">
                <div className="resultValue">
                  <SlotNumber
                    value={r.netUsd}
                    decimals={2}
                    prefix="$"
                    suffix="/total"
                  />
                </div>
              </div>

              <div className="resultCell resultCellRight">
                {hasLocal ? (
                  <div className="resultValue">
                    <SlotNumber
                      value={Number(r.netLocal)}
                      decimals={2}
                      suffix="/total"
                    />
                  </div>
                ) : (
                  <div className="resultValue resultMuted">—</div>
                )}
              </div>

              <div className="resultCell">
                <div className="resultValue">
                  <SlotNumber
                    value={weightKg ? r.netUsd / weightKg : 0}
                    decimals={2}
                    prefix="$"
                    suffix="/kg"
                  />
                </div>
              </div>

              <div className="resultCell resultCellRight">
                {hasLocal ? (
                  <div className="resultValue">
                    <SlotNumber
                      value={weightKg ? Number(r.netLocal) / weightKg : 0}
                      decimals={2}
                      suffix="/kg"
                    />
                  </div>
                ) : (
                  <div className="resultValue resultMuted">—</div>
                )}
              </div>
            </div>

            <div className="sep" />

            <div className="smallNote">
              100% value: <b>${fmtNum(r.value100Usd ?? r.grossUsd, 2)}</b>
              {" • "}
              Price to pay: <b>${fmtNum(r.priceToPayUsd ?? r.netUsd, 2)}</b>
              {" • "}
              Profit:{" "}
              <b>
                ${fmtNum(r.profitUsd ?? (r.grossUsd || 0) - (r.netUsd || 0), 2)}
              </b>
              {weightKg ? (
                <>
                  {" • "}
                  Profit/kg:{" "}
                  <b>
                    $
                    {fmtNum(
                      r.profitUsdPerKg ??
                        ((r.grossUsd || 0) - (r.netUsd || 0)) / weightKg,
                      2,
                    )}
                  </b>
                </>
              ) : null}
              <br />
              Margin/kg: <b>${fmtNum(r.marginUsdPerKg, 2)}</b>
              {" • "}
              Margin total: <b>${fmtNum(r.marginTotalUsd, 2)}</b>
              {" • "}
              Extra scale applied:{" "}
              <b>{fmtNum((r.extraScaleApplied ?? 1) * 100, 2)}%</b>
            </div>
            {r?.deductionsUsd?.totalDeductionByMetalUsd ? (
              <>
                <div className="sep" />
                <div className="sectionTitle">Deductions by metal (USD)</div>

                <div className="tableWrap">
                  <table className="table" style={{ minWidth: 520 }}>
                    <thead>
                      <tr>
                        <th>Metal</th>
                        <th>User deduction</th>
                        <th>Extra margin deduction</th>
                        <th>Total deduction</th>
                        <th>Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {["pt", "pd", "rh"].map((k) => (
                        <tr key={k}>
                          <td style={{ fontWeight: 800 }}>{k.toUpperCase()}</td>
                          <td>
                            $
                            {fmtNum(
                              r.deductionsUsd.userDeductionByMetalUsd?.[k] ?? 0,
                              2,
                            )}
                          </td>
                          <td>
                            $
                            {fmtNum(
                              r.deductionsUsd.extraMarginDeductionByMetalUsd?.[
                                k
                              ] ?? 0,
                              2,
                            )}
                          </td>
                          <td style={{ fontWeight: 800 }}>
                            $
                            {fmtNum(
                              r.deductionsUsd.totalDeductionByMetalUsd?.[k] ??
                                0,
                              2,
                            )}
                          </td>
                          <td style={{ fontWeight: 800 }}>
                            $
                            {fmtNum(
                              r.netByMetalUsd?.[k] ??
                                r.finalNetByMetalUsd?.[k] ??
                                0,
                              2,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="smallNote" style={{ marginTop: 8 }}>
                  Total user deduction:{" "}
                  <b>
                    ${fmtNum(r.deductionsUsd.totalUserDeductionUsd ?? 0, 2)}
                  </b>
                  {" • "}
                  Total extra margin deduction:{" "}
                  <b>
                    $
                    {fmtNum(
                      r.deductionsUsd.totalExtraMarginDeductionUsd ?? 0,
                      2,
                    )}
                  </b>
                  {" • "}
                  Total deduction:{" "}
                  <b>${fmtNum(r.deductionsUsd.totalDeductionUsd ?? 0, 2)}</b>
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <div className="smallNote">Enter values and calculate.</div>
        )}
      </div>

      {/* Saved entries */}
      <div className="card gridFullWidth">
        <div className="cardHeader">
          <h2>Saved entries</h2>
          <div className="smallNote">
            {loadingEntries ? "Loading…" : `${entries.length} shown`}
          </div>
        </div>

        <div className="adminToolbar" style={{ marginBottom: 10 }}>
          <input
            placeholder="Search label…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="desc">Newest</option>
            <option value="asc">Oldest</option>
          </select>
          <select value={cap} onChange={(e) => setCap(Number(e.target.value))}>
            <option value={10}>Last 10</option>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
          </select>
          <button type="button" onClick={() => loadEntries(true)}>
            Refresh
          </button>
        </div>

        {/* Desktop table */}
        <div className="tableWrap desktopOnly">
          <table className="table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Label</th>
                <th>Weight</th>
                <th>Net USD</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((x) => (
                <tr key={x._id}>
                  <td>
                    {x.createdAt ? new Date(x.createdAt).toLocaleString() : "—"}
                  </td>
                  <td style={{ fontWeight: 800 }}>{x.label || "—"}</td>
                  <td>
                    {fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}
                  </td>
                  <td style={{ fontWeight: 800 }}>
                    ${fmtNum(x.result?.netUsd, 2)}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => loadEntryIntoForm(x)}
                      >
                        Load
                      </button>

                      <button
                        type="button"
                        onClick={() => setConfirmDelete(x)}
                        disabled={deletingId === x._id}
                      >
                        {deletingId === x._id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!entries.length ? (
                <tr>
                  <td colSpan={5} className="smallNote">
                    No entries (or expired after 72h).
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {nextCursor ? (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                type="button"
                onClick={() => loadEntries(false)}
                disabled={loadingEntries}
              >
                {loadingEntries ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </div>

        {/* Mobile cards */}
        <div className="mobileOnly">
          {!entries.length ? (
            <div className="smallNote">No entries (or expired after 72h).</div>
          ) : (
            entries.map((x) => (
              <div className="mRow" key={x._id}>
                <div className="mRowTop">
                  <div>
                    <div className="mRowTitle">{x.label || "—"}</div>
                    <div className="mRowMeta">
                      {x.createdAt
                        ? new Date(x.createdAt).toLocaleString()
                        : "—"}
                    </div>
                  </div>
                  <div className="mStrong">${fmtNum(x.result?.netUsd, 2)}</div>
                </div>

                <div className="mGrid">
                  <div className="mField">
                    <div className="mLabel">Weight</div>
                    <div className="mValue">
                      {fmtNum(x.input?.weight, 0)} {x.input?.weightUnit}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 10,
                    flexWrap: "wrap",
                  }}
                >
                  <button type="button" onClick={() => loadEntryIntoForm(x)}>
                    Load
                  </button>

                  <button
                    type="button"
                    onClick={() => setConfirmDelete(x)}
                    disabled={deletingId === x._id}
                  >
                    {deletingId === x._id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {toast ? (
        <div
          className={`toast ${toast.type === "err" ? "toastErr" : "toastOk"}`}
        >
          {toast.text}
        </div>
      ) : null}

      {showSettings ? (
        <div
          className="modalBackdrop"
          onMouseDown={() => setShowSettings(false)}
        >
          <div className="modalCard" onMouseDown={(e) => e.stopPropagation()}>
            <div className="cardHeader" style={{ marginBottom: 10 }}>
              <h2 style={{ margin: 0 }}>Percent calculator settings</h2>
              <button
                type="button"
                className="iconBtn btnGlass"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
                title="Close"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <div className="smallNote" style={{ marginBottom: 10 }}>
              Margin is applied as a required deduction across all metals (if
              your chosen % kept does not meet it).
            </div>

            <label>Margin per kilogram (USD)</label>
            <input
              type="number"
              value={settings?.marginUsdPerKg ?? ""}
              onChange={(e) => {
                setPricingMode("MARGIN");
                setSettings({
                  ...(settings || {}),
                  marginUsdPerKg: e.target.value,
                });
              }}
              placeholder="e.g. 8"
            />
            <label style={{ display: "block", marginBottom: 6 }}>LME input unit</label>
            <div className="customSelect">
              <button
                ref={refLmeTrigger}
                type="button"
                className="customSelectTrigger"
                onClick={() => setLmeOpen((v) => !v)}
                onKeyDown={(e) => onSelectTriggerEnter(e, lmeOpen, setLmeOpen, refLmeOptOz)}
                aria-haspopup="listbox"
                aria-expanded={lmeOpen}
              >
                {(settings?.lmeUnit || "per_oz") === "per_oz" ? "LME per Toz" : "LME per g"}
                <span className="chev">▾</span>
              </button>

              {lmeOpen && (
                <div className="customSelectMenu" role="listbox" aria-label="LME unit">
                  <div
                    ref={refLmeOptOz}
                    tabIndex={0}
                    role="option"
                    aria-selected={(settings?.lmeUnit || "per_oz") === "per_oz"}
                    className={`customOption ${(settings?.lmeUnit || "per_oz") === "per_oz" ? "active" : ""}`}
                    onClick={() => {
                      const next = "per_oz";
                      setSettings({ ...(settings || {}), lmeUnit: next });
                      setLmeUnit(next); // keep local state aligned immediately
                      setLmeOpen(false);
                      focusNext(refWeightTrigger);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        const next = "per_oz";
                        setSettings({ ...(settings || {}), lmeUnit: next });
                        setLmeUnit(next);
                        setLmeOpen(false);
                        focusNext(refWeightTrigger);
                      }
                    }}
                  >
                    LME per Toz
                  </div>

                  <div
                    tabIndex={0}
                    role="option"
                    aria-selected={(settings?.lmeUnit || "per_oz") === "per_g"}
                    className={`customOption ${(settings?.lmeUnit || "per_oz") === "per_g" ? "active" : ""}`}
                    onClick={() => {
                      const next = "per_g";
                      setSettings({ ...(settings || {}), lmeUnit: next });
                      setLmeUnit(next);
                      setLmeOpen(false);
                      focusNext(refWeightTrigger);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        const next = "per_g";
                        setSettings({ ...(settings || {}), lmeUnit: next });
                        setLmeUnit(next);
                        setLmeOpen(false);
                        focusNext(refWeightTrigger);
                      }
                    }}
                  >
                    LME per g
                  </div>
                </div>
              )}
            </div>

            <label style={{ display: "block", marginBottom: 6, marginTop: 10 }}>Weight unit</label>
            <div className="customSelect">
              <button
                ref={refWeightTrigger}
                type="button"
                className="customSelectTrigger"
                onClick={() => setWeightOpen((v) => !v)}
                onKeyDown={(e) => onSelectTriggerEnter(e, weightOpen, setWeightOpen, refWeightOptG)}
                aria-haspopup="listbox"
                aria-expanded={weightOpen}
              >
                {(settings?.weightUnit || "g") === "g" ? "grams" : "kg"}
                <span className="chev">▾</span>
              </button>

              {weightOpen && (
                <div className="customSelectMenu" role="listbox" aria-label="Weight unit">
                  <div
                    ref={refWeightOptG}
                    tabIndex={0}
                    role="option"
                    aria-selected={(settings?.weightUnit || "g") === "g"}
                    className={`customOption ${(settings?.weightUnit || "g") === "g" ? "active" : ""}`}
                    onClick={() => {
                      const next = "g";
                      setSettings({ ...(settings || {}), weightUnit: next });
                      setWeightUnit(next);
                      setWeightOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        const next = "g";
                        setSettings({ ...(settings || {}), weightUnit: next });
                        setWeightUnit(next);
                        setWeightOpen(false);
                      }
                    }}
                  >
                    grams
                  </div>

                  <div
                    tabIndex={0}
                    role="option"
                    aria-selected={(settings?.weightUnit || "g") === "kg"}
                    className={`customOption ${(settings?.weightUnit || "g") === "kg" ? "active" : ""}`}
                    onClick={() => {
                      const next = "kg";
                      setSettings({ ...(settings || {}), weightUnit: next });
                      setWeightUnit(next);
                      setWeightOpen(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === "NumpadEnter") {
                        e.preventDefault();
                        const next = "kg";
                        setSettings({ ...(settings || {}), weightUnit: next });
                        setWeightUnit(next);
                        setWeightOpen(false);
                      }
                    }}
                  >
                    kg
                  </div>
                </div>
              )}
            </div>


            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 14,
              }}
            >
              <button type="button" onClick={() => setShowSettings(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btnPrimary"
                onClick={saveSettings}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div
          className="modalBackdrop"
          onMouseDown={() => setConfirmDelete(null)}
        >
          <div className="modalCard" onMouseDown={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Delete entry?</h3>

            <div className="smallNote" style={{ marginBottom: 12 }}>
              This will permanently delete <b>{confirmDelete.label || "—"}</b>.
            </div>

            <div
              style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
            >
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deletingId === confirmDelete._id}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btnPrimary"
                onClick={() => deleteEntry(confirmDelete)}
                disabled={deletingId === confirmDelete._id}
              >
                {deletingId === confirmDelete._id ? "Deleting…" : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
